// ONE ANSWER TO "CAN A WALKER STAND HERE?"
//
// Fifteen sites in this project encoded tile passability and six of them were
// independent models: `Game.solid`, `world.passable`, `gate.mjs walkable`,
// `hollows.BARE`, `freeThePlayer`'s `barrier()`, and `soak.mjs`'s local
// `pass()`. Every softlock this project has shipped — round 8's wedged wake,
// round 9's dried shallows, the rescue that walked a charmless player through
// a tier gate — was two of those six giving different answers to the same
// question. So there is one, here, and the others call it.
//
// This is the OVERWORLD/on-foot rule for a plain tile. `Game.solid` is the
// live version: it adds the things only a running game knows — an actor's
// body, shots and wisps that cross water, the Silt Lung going under a hedge
// rooted in water, and what a pod launch flies over. Those are documented
// there, and a check holds this function and `Game.solid` to the same answer
// for every tile value and every charm set.
import { T } from './art.ts';

/** Tiles anything on foot can walk, with nothing in hand. */
export const OPEN = new Set([
  T.GROUND, T.SAND, T.MOSS, T.DEBRIS, T.SCORCH, T.TUFT, T.BONES, T.ICE,   // ice: a pool gone hard is ground to anything on foot (LS-381)
  T.FORD,   // a tidal crossing at low water (LS-383): the tide writes it as water when the water is up
  // a cut stair between two plateaus of the same tier: walkable by anyone,
  // because the crossing grants nothing that tier does not already have
  T.RAMP,
]);

/** Which charm opens which barrier. The whole gating system is these three
 *  rows; everything else is which tier hands out which charm. */
export const OPENED_BY = {
  pods: [T.RUBBLE],
  ember: [T.THORN],
  // Deep water AND shallows. Shallows are NOT wadeable scenery — that reading
  // dried 13 of 200 seeds into dead runs, because `openFlood` turned water
  // into shallows and a charmless player cannot cross them.
  tide: [T.WATER, T.SHALLOW],
};

/* ONE ANSWER TO "IS THIS TILE WET?", FOR THE SAME REASON.
 *
 * Seven sites spelled `v === T.WATER || v === T.SHALLOW` by hand: the water
 * mask, the grass waterline, the hollow's flood repair, the overworld thicket
 * band, and three in `game.ts` including a local `wet` arrow declared inline.
 * That is one fact about the world written out seven times, and the file
 * above exists because exactly this shape — the same question answered
 * independently in six places — is where every softlock this project has
 * shipped came from.
 *
 * It is derived from `OPENED_BY.tide` rather than written again, which is the
 * point: the tide charm opens the wet tiles, so "which tiles are wet" and
 * "which tiles does the tide open" are the SAME LIST and cannot drift. A
 * third water tile added to that row is wet everywhere, in one edit, or the
 * shallows dry into dead runs again the way they did in round 9.
 */
/* ...PLUS THE SEA, WHICH IS WET AND IS NOT A GATE.
 *
 * The rule above — that "which tiles are wet" and "which tiles the tide opens"
 * are the same list — held while every body of water in the game was something
 * the tide was meant to let you cross. The overworld is an island now, and the
 * ocean round it is not a lock: opening it hands the player a route to every
 * plateau that touches the coast, which is every cliff and every ramp in the
 * world bypassed by one charm.
 *
 * So `T.SEA` joins the WET list, which is about ART and ambience — it is drawn
 * as deep water, it makes the water mask, it grows the same reeds — and stays
 * out of `OPENED_BY`, which is about PASSAGE. The two lists were one list to
 * stop them drifting; they are two now for a stated reason, and the thing that
 * keeps them honest is that the sea is the only member of the difference. */
export const WET = new Set([...OPENED_BY.tide, T.SEA]);

/** Is this tile water of any depth? */
export function isWet(v) { return WET.has(v); }

// TWO QUESTIONS, ONE TABLE. They are not the same question and conflating
// them is its own bug: holding the pods does not let you WALK THROUGH cracked
// stone, it lets you get past it — you still have to throw one. The tide is
// the odd one out, and that is the point of it: it is the only charm that
// makes its barrier something you simply move through.
const CROSSED_ON_FOOT = { tide: true };

/**
 * Can someone on foot STAND on this tile right now? The live rule — what
 * `Game.solid` answers for a plain tile.
 * @param {number} v      a tile value
 * @param {object} has    { pods, tide, ember } — truthy for each charm held
 */
export function walkableTile(v, has: any = {}) {
  if (OPEN.has(v)) return true;
  for (const charm of Object.keys(OPENED_BY)) {
    if (!CROSSED_ON_FOOT[charm]) continue;
    if (has[charm] && OPENED_BY[charm].includes(v)) return true;
  }
  return false;
}

/**
 * Can someone holding these charms GET PAST this tile at all — by walking,
 * blasting or burning? The proof rule: what a reachability flood asks.
 * @param {number} v      a tile value
 * @param {object} has    { pods, tide, ember } — truthy for each charm held
 */
export function reachableTile(v, has: any = {}) {
  if (walkableTile(v, has)) return true;
  for (const charm of Object.keys(OPENED_BY)) {
    if (has[charm] && OPENED_BY[charm].includes(v)) return true;
  }
  return false;
}

/** The barriers a charm opens — the tiles that are a GATE rather than
 *  scenery. Used by anything that must refuse to cross one. */
// the GATES: what a tier's charm opens at a ramp
export const BARRIERS = new Set([...OPENED_BY.pods, ...OPENED_BY.ember, ...OPENED_BY.tide]);
